import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@indeed/ifl-components';
import AutoApplyNavigation from './AutoApplyNavigation';

/**
 * Layout component for Auto Apply flow
 * Mobile-first design constrained to maxWidth: 450px.
 *
 * The layout intentionally does NOT lock itself to the viewport height: the
 * page grows with its content and the window/body scrolls, giving natural
 * mobile scrolling (URL-bar collapse, body momentum/overscroll) consistent with
 * the Auto Apply Setup flow. Pages that need a pinned header or footer use
 * position: sticky / fixed relative to the viewport (e.g. the review page's
 * sticky JobInfoHeader and fixed Previous/Next footer).
 */
const AutoApplyLayout: React.FC = () => {
    return (
        <Box
            sx={{
                backgroundColor: 'color.background.page',
                inlineSize: '100%',
                display: 'flex',
                justifyContent: 'center',
                // Clip horizontal overflow from PageTransition's slide animation
                // WITHOUT establishing a vertical scroll container. `overflow-x:
                // clip` (unlike `overflow: hidden`) is not a scroll container, so
                // the window stays the scroll owner (natural mobile scrolling) and
                // the review page's `position: sticky` header pins to the viewport.
                overflowX: 'clip',
            }}
        >
            {/* Mobile-first container with fixed maxWidth */}
            <Box
                sx={{
                    inlineSize: '100%',
                    maxInlineSize: '450px',
                    // Keep the flow at least one viewport tall so the page
                    // background fills the screen and pages don't have to force
                    // their own `100vh` (which would stack *below* the nav and
                    // overflow the viewport by the nav's height). Grows past the
                    // viewport when content is taller, so the window still
                    // scrolls naturally.
                    minBlockSize: '100dvh',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    backgroundColor: 'color.background.page',
                }}
            >
                {/* Render navigation directly in layout */}
                <AutoApplyNavigation />

                {/* Page content fills the height remaining below the nav. The
                    in-flow child (each page's PageTransition wrapper) is
                    stretched so a page using `minBlockSize: 100%` fills exactly
                    the viewport minus the nav — no more, no less. Fixed footers
                    (e.g. the review Previous/Next bar) are out of flow and
                    unaffected. */}
                <Box
                    sx={{
                        inlineSize: '100%',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        '& > *': { flexGrow: 1 },
                    }}
                >
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
};

export default AutoApplyLayout;
